import { dateTo_DD_MM_YYYY_dot, dateTo_MM_YYYY_slash } from 'cpool-utils';

import type { RangeDateValue, SingleDateValue } from './types';

export const formatDateRange = (range: RangeDateValue): string => {
    if (!range?.from) return '';
    if (!range.to) return dateTo_DD_MM_YYYY_dot(range.from);
    return `${dateTo_DD_MM_YYYY_dot(range.from)} - ${dateTo_DD_MM_YYYY_dot(range.to)}`;
};

export const formatSingleDate = (date: SingleDateValue): string => {
    if (!date) return '';
    return dateTo_DD_MM_YYYY_dot(date);
};
export const formatSingleMonth = (date: SingleDateValue): string => {
    if (!date) return '';
    return dateTo_MM_YYYY_slash(date);
};

/** Converts a date to UTC midnight to preserve calendar date when serializing to ISO. */
export const toUtcMidnight = (date: Date): Date =>
    new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

export const parseDate = (dateString: string, maxAllowedYear?: number): Date | null => {
    const trimmed = dateString.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    if (!match) return null;

    const [, dayStr, monthStr, yearStr] = match;
    const day = parseInt(dayStr, 10);
    const month = parseInt(monthStr, 10) - 1;
    const year = parseInt(yearStr, 10);

    if (month < 0 || month > 11) return null;
    if (day < 1 || day > 31) return null;
    if (year < 1900 || year > (maxAllowedYear ?? 2100)) return null;

    const date = new Date(Date.UTC(year, month, day));
    if (date.getUTCDate() !== day || date.getUTCMonth() !== month || date.getUTCFullYear() !== year) {
        return null;
    }

    return date;
};

export const parseMonth = (monthString: string, maxAllowedYear?: number): Date | null => {
    const trimmed = monthString.trim();
    if (!trimmed) return null;

    const match = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
    if (!match) return null;

    const [, monthStr, yearStr] = match;
    const month = parseInt(monthStr, 10) - 1;
    const year = parseInt(yearStr, 10);

    if (month < 0 || month > 11) return null;
    if (year < 1900 || year > (maxAllowedYear ?? 2100)) return null;

    return new Date(Date.UTC(year, month, 1));
};

export const formatDateInput = (input: string): string => {
    const digits = input.replace(/\D/g, '');
    const limited = digits.slice(0, 8);
    let formatted = '';
    for (let i = 0; i < limited.length; i++) {
        if (i === 2 || i === 4) {
            formatted += '.';
        }
        formatted += limited[i];
    }
    return formatted;
};

export const formatMonthInput = (input: string): string => {
    const digits = input.replace(/\D/g, '');
    const limited = digits.slice(0, 6);
    let formatted = '';
    for (let i = 0; i < limited.length; i++) {
        if (i === 2) {
            formatted += '/';
        }
        formatted += limited[i];
    }
    return formatted;
};

/** Counts digits before `index` in `value`, used to keep the caret anchored to the same digit across reformatting. */
export const countDigitsBeforeIndex = (value: string, index: number): number =>
    value.slice(0, index).replace(/\D/g, '').length;

/** Finds the caret index in `formatted` that sits right after the `digitCount`-th digit. */
export const getCursorPositionForDigitCount = (formatted: string, digitCount: number): number => {
    if (digitCount <= 0) return 0;

    let seen = 0;
    for (let i = 0; i < formatted.length; i++) {
        if (/\d/.test(formatted[i])) {
            seen += 1;
            if (seen === digitCount) return i + 1;
        }
    }
    return formatted.length;
};
